// Next.js imports
import dynamic from "next/dynamic";

// Sanity.io imports
import {
  PortableText,
  toPlainText,
  type PortableTextComponents,
} from "next-sanity";

// Component & Template imports
import ErrorBoundary from "@/components/ErrorBoundary";
import Gist from "@/components/interactive/Gist";
import NextLink from "@/components/interactive/NextLink";
import MdxRenderer from "@/components/layout/MdxRenderer";
import GalleryImage from "@/components/interactive/GalleryImage";
import SectionPlaceholder from "@/templates/SectionPlaceholder";
import Agenda from "@/templates/Agenda";
import ContentSwitcher from "@/templates/ContentSwitcher";
import BodyCTA from "@/templates/BodyCTA";
import Card from "@/templates/Card";

// Hooks, Utility & Helper function imports
import slugify from "@/utils/slugify";
import classNames from "@/utils/classNames";
import { IsovButton } from "@/components/stencil";

// Dynamic Component Imports
const AsciinemaPlayer = dynamic(
  () => import("@/components/interactive/AsciinemaPlayer"),
  { ssr: false },
);
const PhotoGallery = dynamic(() => import("@/templates/PhotoGallery"));
const Video = dynamic(() => import("@/components/interactive/Video"));

import { DEFAULT_LOCALE } from "@/utils/localeConstants";
import { t } from "@/utils/t";

const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;

export function createPortableTextComponents(
  locale: string = DEFAULT_LOCALE,
): PortableTextComponents {
  return {
    types: {
      mediaGallery: ({ value }) => {
        // console.log("mediaGallery:", value);
        return <PhotoGallery {...value} />;
      },
      mediaImage: ({ value }) => {
        // console.log("mediaImage:", value);
        return <GalleryImage {...value} />;
      },
      mediaVideo: ({ value }) => {
        // console.log("mediaVideo:", value);
        return (
          <div className="mb-6">
            <Video
              theme={value?.theme}
              slot="media"
              videoId={t(value?.videoId, locale)}
              playlistId={t(value?.playlistId, locale)}
              startAt={t(value?.startAt, locale)}
              title={value?.title}
            />
          </div>
        );
      },
      bodyCta: ({ value }) => {
        // console.log("bodyCta:", value);
        return (
          <BodyCTA
            theme={value?.theme || "primary"}
            eyebrow={value?.eyebrow}
            // headline={value?.headline}
            subtext={value?.headline || value?.subtext}
            description={value?.text}
            alignment={value?.alignment}
            decorated={value?.decorated}
            shaded={value?.shaded ?? true}
            backgroundImage={value?.backgroundImage}
            formId={value?.formId}
            minHeight={value?.minHeight}
            webexMeetingId={value?.webexMeetingId}
            redirectUrl={value?.redirectUrl}
            ctas={value?.ctas}
            icon={value?.icon}
            logo={value?.logo}
            logoWhite={value?.logoWhite}
            showPattern={value?.showPattern}
            locale={locale}
          />
        );
      },
      cta: ({ value }) => {
        // console.log("cta:", value);
        return (
          <NextLink
            href={value?.url}
            className="inline-block"
            type={`button_${value?.variant ?? "default"}`}
          >
            <IsovButton
              theme={value?.theme || "primary"}
              variant={value?.variant}
              label={t(value?.label, locale)}
              className="inline-block"
            />
          </NextLink>
        );
      },
      codeBlock: ({ value }) => {
        // console.log("codeBlock:", value);
        return value?.lineHighlight ? (
          <pre
            style={
              value?.restrictHeight && value?.lineRestriction
                ? {
                    maxHeight: `calc((1.563rem * ${value?.lineRestriction}) + 0.85rem)`,
                  }
                : {}
            }
            className={`${value?.lineNumbers == "show" ? "line-numbers language" : "language"}-${value?.language}`}
            data-line={value?.lineHighlight}
            tabIndex={0}
          >
            <code data-prismjs-copy="Copy All">{value?.code}</code>
          </pre>
        ) : (
          <pre
            style={
              value?.restrictHeight && value?.lineRestriction
                ? {
                    maxHeight: `calc((1.563rem * ${value?.lineRestriction}) + 0.85rem)`,
                  }
                : {}
            }
            className={`${value?.lineNumbers == "show" ? "line-numbers language" : "language"}-${value?.language}`}
            tabIndex={0}
          >
            <code data-prismjs-copy="Copy All">{value?.code}</code>
          </pre>
        );
      },
      gistEmbed: ({ value }) => {
        // console.log("gistEmbed:", value);
        return <Gist gistFile={value?.gistFile} />;
      },
      asciinemaPlayer: ({ value }) => {
        return (
          <ErrorBoundary>
            <AsciinemaPlayer
              src={`${baseUrl}api/asciinema/${value?.castId}`}
              loop
            />
          </ErrorBoundary>
        );
      },
      section: ({ value }) => {
        // console.log("section:", value);
        return (
          <div className="mb-10">
            <SectionPlaceholder
              theme={value?.theme}
              eyebrow="Section Placeholder"
              title={value?.title}
              description={value?.description}
            />
          </div>
        );
      },
      table: ({ value }) => {
        // console.log("table:", value);
        const firstRow = value?.rows[0];
        const alignTop = value?.alignTop;
        const topHeader = value?.hasTopHeader;
        const leftHeader = value?.hasLeftHeader;
        const hLines = value?.hasHLines ?? true;
        const vLines = value?.hasVLines ?? true;

        return (
          <div className="relative w-full overflow-x-auto">
            <table
              className={classNames(
                hLines && "border-bottom",
                vLines && "border-right",
              )}
            >
              {topHeader && (
                <thead>
                  <tr>
                    {firstRow?.cells?.map((cell: any, cellIndex: number) => (
                      <th key={`th-${firstRow?._key}-${cellIndex}`}>
                        <MdxRenderer content={cell} noSpacing />
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {value?.rows?.map((row: any, rowIndex: number) => {
                  if (topHeader && rowIndex === 0) return;
                  return (
                    <tr key={`tr-${row?._key}`}>
                      {row?.cells?.map((cell: any, cellIndex: number) => {
                        const Cell =
                          leftHeader && cellIndex === 0 ? "th" : "td";

                        return (
                          <Cell
                            key={`cell-${row?._key}-${cellIndex}`}
                            className={classNames(alignTop && "align-top")}
                          >
                            <MdxRenderer content={cell} />
                          </Cell>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      },
      richTable: ({ value }) => {
        const headerRows = value?.headerRows ?? 0;
        return (
          <div className="relative w-full overflow-x-auto">
            <table className="rich-table">
              <tbody>
                {value?.rows?.map((row: any, rowIndex: number) => (
                  <tr key={row?._key}>
                    {row?.cells?.map((cell: any, cellIndex: number) => {
                      const Cell = rowIndex < headerRows ? "th" : "td";
                      return (
                        <Cell key={cell?._key ?? cellIndex}>
                          <PortableText
                            value={cell?.value}
                            components={createPortableTextComponents(locale)}
                          />
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      },
      card: ({ value }) => {
        // console.log("card:", value);
        return <Card {...value} locale={locale} />;
      },
      content: ({ value }) => {
        // console.log("card:", value);
        return <PortableText value={t(value, locale)} />;
      },
      agenda: ({ value }) => {
        // console.log("agenda:", value);
        return <Agenda {...value} locale={locale} />;
      },
      contentSwitcher: ({ value }) => {
        // console.log("contentSwitcher:", value);
        return <ContentSwitcher {...value} locale={locale} />;
      },
      undefined: ({ value }) => {
        // console.log("undefined:", value);
        return (
          <div className="mb-10">
            <SectionPlaceholder
              theme={value?.theme}
              eyebrow="Undefined Type"
              title={value?.title}
              description={value?.description}
            />
          </div>
        );
      },
    },

    block: {
      h2: ({ children, value }) => {
        const plainValue = toPlainText(value);
        const slug = slugify(plainValue);
        return (
          <h2 id={slug}>
            <NextLink
              href={`#${slug}`}
              title={plainValue}
              className="jump-link"
              isHyperlink
              showIcons
            >
              {children}
            </NextLink>
          </h2>
        );
      },
      h3: ({ children, value }) => {
        const plainValue = toPlainText(value);
        const slug = slugify(plainValue);
        return (
          <h3 id={slug}>
            <NextLink
              href={`#${slug}`}
              title={plainValue}
              isHyperlink
              showIcons
            >
              {children}
            </NextLink>
          </h3>
        );
      },
      small: ({ children }) => (
        <h6>
          <small>{children}</small>
        </h6>
      ),
      blockquote: ({ children }) => (
        <div className="mb-10">
          <blockquote>
            <svg
              className="w-8 h-8 text-stone mb-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 18 14"
            >
              <path d="M6 0H2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4v1a3 3 0 0 1-3 3H2a1 1 0 0 0 0 2h1a5.006 5.006 0 0 0 5-5V2a2 2 0 0 0-2-2Zm10 0h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4v1a3 3 0 0 1-3 3h-1a1 1 0 0 0 0 2h1a5.006 5.006 0 0 0 5-5V2a2 2 0 0 0-2-2Z" />
            </svg>
            {children}
          </blockquote>
        </div>
      ),
    },

    marks: {
      code: ({ children, value }) => {
        return (
          <code className="bg-slate text-black rounded-small p-1 whitespace-normal">
            {children}
          </code>
        );
      },
      sup: ({ children }) => <sup>{children}</sup>,
      link: ({ children, value }) => {
        const rel = !value?.href?.startsWith("/")
          ? "noreferrer noopener"
          : undefined;
        return (
          <NextLink
            href={value?.href}
            target={value?.openInNewTab ? "_blank" : "_self"}
            rel={rel}
            showIcons
          >
            {children}
          </NextLink>
        );
      },
    },

    list: {
      bullet: ({ children }) => <ul className="mt-xl">{children}</ul>,
      number: ({ children }) => <ol className="mt-lg">{children}</ol>,
      checkmarks: ({ children }) => (
        <ol className="m-auto text-lg">{children}</ol>
      ),
    },

    listItem: {
      bullet: ({ children }) => <li>{children}</li>,
      number: ({ children }) => <li>{children}</li>,
      checkmarks: ({ children }) => <li>✅ {children}</li>,
    },
  };
}

export default createPortableTextComponents();
